import * as z from "@/lib/zod";
import { CELL_KEY, EVENT_ID, SLUG, UNKNOWN_KIND, expandLinks, isBetLinks, isCount, isEntityKey, isOdds, isProbability, isUsd, playerOf, propsRowOf, sniperRowOf, type BetLinksWire } from "./book-rows";
import {
  END_REASONS, FAMILY_LAYOUTS, IN_PLAY, LIVE_PAUSE_KINDS, LIVE_PERIOD_UNITS, PERIOD_BREAK, ladderLineId, topicKey,
  type BoardRow, type Catalog, type Dfs, type DfsLeague, type Board, type BookCell, type EndReason, type EventHeader, type EventId, type Ladder,
  type Discrepancies, type DiscrepancyBook, type DiscrepancyRow,
  type InplayCell, type InplayEvent, type InplayGame, type InplayList, type InplayStatus, type LiveClock, type LivePause, type LivePeriod, type LivePlay, type LiveSets,
  type Arbitrage, type ArbLeg, type ArbPick, type ArbRow, type LowHold, type LowHoldRow,
  type BookId, type CellKey, type WhalesCell, type WhalesLadder, type WhalesSide, type WhalesSummary,
  type LadderLine, type LeagueId, type LeagueSummary, type Props, type PropsRow, type Race, type RaceList, type RowId, type SkippedFamily, type Sniper, type SniperRow, type SniperRowsTopic, type Topic,
  type WhaleDetail, type WhaleList, type WhaleRow, type WhaleState,
} from "./types";
import { EXCHANGE_BOOKS } from "@/lib/book-stake";
import type { APIOpportunity, BookData, SideData } from "@/lib/whale-watch/whale-watch-shared";

const slugSchema = z.string().regex(SLUG);
const eventIdSchema = z.string().regex(EVENT_ID);
const utf8 = new TextEncoder();
const entityKeySchema = z.string().refine(isEntityKey);
const epochMs = z.number().int().nonnegative();
const oddsSchema = z.number().refine(isOdds, "expected a decimal price above 1");
const lineSchema = z.number();
const usd = z.number().refine(isUsd, "expected a dollar amount of 0 or more");
const count = z.number().refine(isCount, "expected a whole count");
const probability = z.number().refine(isProbability, "expected a probability from 0 to 1");
const whaleRowIdSchema = z.string().min(1).refine(
  (value) => !/\p{Cc}/u.test(value) && utf8.encode(value).length <= 512,
);

const wireTopicSchema = z.union([
  z.tuple([z.literal("catalog")]),
  z.tuple([z.literal("dfs")]),
  z.tuple([z.literal("board"), slugSchema, slugSchema]),
  z.tuple([z.literal("ladder"), slugSchema, eventIdSchema, slugSchema, entityKeySchema]),
  z.tuple([z.literal("props"), slugSchema, slugSchema]),
  z.tuple([z.literal("optimizer"), slugSchema, slugSchema]),
  z.tuple([z.literal("whale"), slugSchema, z.enum(["default", "wide"])]),
  z.tuple([z.literal("whale_row"), whaleRowIdSchema]),
  z.tuple([z.literal("discrepancies"), slugSchema]),
  z.tuple([z.literal("arbitrage"), slugSchema]),
  z.tuple([z.literal("lowhold"), slugSchema, z.enum(["default", "wide"])]),
  z.tuple([z.literal("sniper"), slugSchema]),
  z.tuple([z.literal("sniper_live"), slugSchema]),
  z.tuple([z.literal("race"), slugSchema]),
  z.tuple([z.literal("live"), slugSchema]),
  z.tuple([z.literal("live_event"), slugSchema, eventIdSchema, slugSchema]),
  z.tuple([z.literal("whales"), slugSchema, eventIdSchema, z.string().regex(CELL_KEY)]),
]);
type WireTopic = z.infer<typeof wireTopicSchema>;

function topicFromWire(wire: WireTopic): Topic {
  switch (wire[0]) {
    case "catalog": return { kind: "catalog" };
    case "dfs": return { kind: "dfs" };
    case "board": return { kind: "board", league: wire[1], family: wire[2] };
    case "ladder": return { kind: "ladder", league: wire[1], event: wire[2], family: wire[3], entity: wire[4] };
    case "props": return { kind: "props", league: wire[1], book: wire[2] };
    case "optimizer": return { kind: "optimizer", league: wire[1], book: wire[2] };
    case "whale": return { kind: "whale", sport: wire[1], tier: wire[2] };
    case "whale_row": return { kind: "whale_row", id: wire[1] };
    case "discrepancies": return { kind: "discrepancies", league: wire[1] };
    case "arbitrage": return { kind: "arbitrage", league: wire[1] };
    case "lowhold": return { kind: "lowhold", league: wire[1], tier: wire[2] };
    case "sniper": return { kind: "sniper", league: wire[1] };
    case "sniper_live": return { kind: "sniper_live", league: wire[1] };
    case "race": return { kind: "race", book: wire[1] };
    case "live": return { kind: "live", league: wire[1] };
    case "live_event": return { kind: "live_event", league: wire[1], event: wire[2], family: wire[3] };
    case "whales": return { kind: "whales", league: wire[1], event: wire[2], key: wire[3] };
  }
}

const raceLegSchema = z.object({
  row: z.string().min(1).nullable(), league: slugSchema.or(z.literal("")), start: epochMs, player: z.string(), pos: z.string(), team: z.string(), opp: z.string(),
  img: z.string().nullable(), market: z.string(), line: z.number().finite(), side: z.string(), fair: z.number().gt(0).lt(1).nullable(), odds: oddsSchema.nullable(),
  ev: z.number().finite().nullable().optional(), assumed: z.boolean().optional(),
  books: z.array(z.tuple([slugSchema, z.number().gt(0).lt(1)])).optional(),
});
export const raceSchema = z.object({
  id: z.string().min(1), book: slugSchema, since: epochMs, mult: z.number().positive(), boost: z.number().positive(),
  seats: count, taken: count, min: usd, max: usd, hit: z.number().min(0).lt(1).nullable(), need: z.number().positive(), ev: z.number().finite().nullable(),
  assumed: count.optional(), legs: z.array(raceLegSchema).min(1),
});
const raceSnapSchema = z.object({ races: z.array(raceSchema) });
const raceDeltaSchema = z.object({ races: z.array(raceSchema), drop: z.array(z.string().min(1)).optional() });

export function toRace(race: z.infer<typeof raceSchema>): Race {
  return {
    id: race.id, book: race.book, sinceMs: race.since, mult: race.mult, boost: race.boost, seats: race.seats, taken: race.taken, min: race.min, max: race.max,
    hit: race.hit, need: race.need, ev: race.ev, assumed: race.assumed ?? 0,
    legs: race.legs.map((leg) => ({
      row: leg.row, league: leg.league, startMs: leg.start, player: leg.player, pos: leg.pos, team: leg.team, opp: leg.opp, img: leg.img,
      market: leg.market, line: leg.line, side: leg.side, fair: leg.fair, odds: leg.odds, ev: leg.ev ?? null, assumed: leg.assumed ?? false,
      books: leg.books?.map(([book, fair]) => ({ book, fair })) ?? null,
    })),
  };
}

export type RaceDelta = { readonly races: readonly Race[]; readonly drop: readonly string[] };

const marketSpecSchema = z.object({
  id: z.string().min(1), label: z.string(), name: z.string().min(1), period: z.string(),
  line: z.enum(["none", "spread", "total"]), outcomes: z.array(z.string().min(1)).min(1).max(3),
  labels: z.array(z.string()).optional(),
  keys: z.array(z.string().min(1)).default([]),
}).refine((spec) => spec.labels === undefined || spec.labels.length === spec.outcomes.length, { message: "labels must match outcomes one to one" });
const familySchema = z.object({
  id: slugSchema, label: z.string(), kind: z.enum(["lines", "props"]), layout: z.enum(FAMILY_LAYOUTS).default("sides"), group: z.string().optional(), category: z.string().min(1),
  events: count, rows: count, markets: z.array(marketSpecSchema).min(1),
}).refine((family) => (family.layout === "results"
  ? family.kind === "lines" && family.markets.length === 1 && family.markets.every((spec) => spec.line === "none" && spec.outcomes.length === 1 && spec.labels?.length === 1 && spec.labels[0] !== "")
  : family.markets.every((spec) => spec.outcomes.length >= 2)), { message: "a family's markets must fit its layout" });
const familyLayoutProbe = z.object({ id: z.string(), layout: z.string() });
const knownLayouts: ReadonlySet<string> = new Set(FAMILY_LAYOUTS);
const familiesSchema = z.array(z.unknown()).transform((items, ctx) => {
  const families: z.output<typeof familySchema>[] = [];
  const skipped: SkippedFamily[] = [];
  items.forEach((item, index) => {
    const probe = familyLayoutProbe.safeParse(item);
    if (probe.success && !knownLayouts.has(probe.data.layout)) {
      skipped.push({ id: probe.data.id, layout: probe.data.layout });
      return;
    }
    const parsed = familySchema.safeParse(item);
    if (parsed.success) {
      families.push(parsed.data);
      return;
    }
    for (const issue of parsed.error.issues) ctx.addIssue({ ...issue, path: [index, ...issue.path] });
  });
  return { families, skipped };
});
const leagueSchema = z.object({
  id: slugSchema, status: z.enum(["live", "warming", "reconnecting"]),
  stale_books: z.array(slugSchema), events: count, families: familiesSchema,
});
const catalogSnapSchema = z.object({
  books: z.array(z.object({ id: slugSchema, stake: z.enum(["depth", "limit"]).optional(), standard: oddsSchema.nullable().catch(null), resells: slugSchema.optional() })),
  leagues: z.array(leagueSchema),
});
const catalogDeltaSchema = z.object({ leagues: z.array(leagueSchema).default([]) });
const dfsSchema = z.object({
  leagues: z.array(z.object({ id: slugSchema, floor: probability, books: z.array(z.object({ id: slugSchema, fair: z.array(probability) })) })),
});

type DfsLeagueEntry = readonly [LeagueId, DfsLeague | null];

function dfsLeagues({ leagues }: z.output<typeof dfsSchema>): DfsLeagueEntry[] {
  return leagues.map((league): DfsLeagueEntry => [
    league.id,
    league.books.length === 0 ? null : { floor: league.floor, books: new Map(league.books.map((book) => [book.id, book.fair])) },
  ]);
}

const livePeriodSchema = z.object({ unit: z.enum(LIVE_PERIOD_UNITS), number: count.default(0), half: z.enum(["top", "bottom"]).optional(), break: z.boolean().optional() })
  .transform((p): LivePeriod => ({ unit: p.unit, number: p.number, half: p.half ?? null, break: p.break ?? false }))
  .nullable().catch(null);
const eventSchema = z.object({ id: eventIdSchema, name: z.string(), start: epochMs, home: z.string(), away: z.string(), home_short: z.string().optional(), away_short: z.string().optional(), home_logo_url: z.string().optional(), away_logo_url: z.string().optional(), home_rank: z.number().int().positive().optional(), away_rank: z.number().int().positive().optional(), home_conf: z.string().min(1).optional(), away_conf: z.string().min(1).optional(), props: count, period: livePeriodSchema.optional() });
const oddsList = z.array(oddsSchema.nullable()).min(1).max(3);
const stakeList = z.array(usd.nullable()).min(1).max(3);
const bookCellSchema = z.tuple([slugSchema, oddsList, lineSchema.nullable().optional(), stakeList.optional()]);
const winShare = z.number().gt(0).lt(1);
const marketSchema = z.object({
  main: lineSchema.optional(), lines: count, books: z.array(bookCellSchema),
  consensus: z.tuple([winShare, winShare]).refine(([a, b]) => Math.abs(a + b - 1) < 1e-3, { message: "a consensus must be two complementary win shares" }).optional(),
}).refine((market) => market.consensus === undefined || market.books.every(([, odds]) => odds.length === 2), {
  message: "a consensus must belong to a two-outcome market",
});
const entitySchema = z.object({
  key: entityKeySchema.min(1), name: z.string(),
  player_id: z.string().optional(), image_url: z.string().optional(), position: z.string().optional(),
});
const boardEntitySchema = entitySchema.extend({ order: z.number().int().optional() });
const rowSchema = z.object({
  id: z.string().min(1), event: eventIdSchema,
  entity: boardEntitySchema.nullable(),
  markets: z.array(marketSchema.nullable()).min(1),
}).refine((row) => {
  const results = row.entity?.order !== undefined;
  return row.markets.every((market) => market === null
    || (market.books.every(([, odds]) => (results ? odds.length === 1 : odds.length >= 2)) && (!results || market.main === undefined)));
}, { message: "a row's prices must fit its layout" });
const boardSnapSchema = z.object({ events: z.array(eventSchema), rows: z.array(rowSchema) });
const boardDeltaSchema = z.object({
  events: z.array(eventSchema).default([]), rows: z.array(rowSchema).default([]),
  drop_rows: z.array(z.string().min(1)).default([]), drop_events: z.array(eventIdSchema).default([]),
});

const betLinksSchema = z.custom<BetLinksWire>(isBetLinks);

const discrepancyRowIdSchema = z.string().regex(/^[^|]+\|[^|]+\|[^|]+$/);
const discrepancyBookSchema = z.tuple([
  slugSchema, lineSchema, oddsSchema.nullable(), oddsSchema.nullable(), z.number().positive().nullable(), z.string(), z.string(), betLinksSchema, betLinksSchema,
  probability.nullable(), probability.nullable(),
]).refine(([, , over, under]) => over !== null || under !== null, { message: "a book needs a price on at least one side" });
const discrepancyRowSchema = z.object({
  id: discrepancyRowIdSchema, event: eventIdSchema, market: z.string(), market_key: z.string().min(1),
  entity: entitySchema, team: z.string(),
  modal: lineSchema, min: lineSchema, max: lineSchema, spread: z.number().nonnegative(),
  books: z.array(discrepancyBookSchema).min(2),
  best: z.object({ book: slugSchema, line: lineSchema, side: z.enum(["over", "under"]), fair: probability.nullable().optional() }),
  fair_modal: z.object({ over: probability, under: probability }).nullable().optional(),
  peers: count,
});
const discrepanciesSnapSchema = z.object({ events: z.array(eventSchema), rows: z.array(discrepancyRowSchema) });
const discrepanciesDeltaSchema = z.object({
  events: z.array(eventSchema).default([]), rows: z.array(discrepancyRowSchema).default([]),
  drop_rows: z.array(z.string().min(1)).default([]), drop_events: z.array(eventIdSchema).default([]),
});

const arbRowIdSchema = z.string().min(1);
const arbEntitySchema = entitySchema.extend({ key: z.string() });
const arbKindSchema = z.enum(["two_way", "threshold", "three_way"]);
const arbLegElevenSchema = z.tuple([slugSchema, z.string(), z.string(), lineSchema, oddsSchema, usd.nullable(), z.string(), betLinksSchema, probability.nullable(), z.boolean(), z.boolean()]);
const arbLegSchema = z.union([
  arbLegElevenSchema,
  z.tuple([slugSchema, z.string(), z.string(), lineSchema, oddsSchema, oddsSchema, usd.nullable(), z.string(), betLinksSchema, probability.nullable(), z.boolean(), z.boolean()])
    .refine(([, , , , odds, effective]) => odds === effective, { message: "a 12-element leg's effective slot must repeat its served odds" }),
]).transform((leg): z.output<typeof arbLegElevenSchema> => {
  if (leg.length === 11) return leg;
  const [book, side, market, line, odds, , ...rest] = leg;
  return [book, side, market, line, odds, ...rest];
});
const arbPickSchema = z.tuple([
  z.array(count), z.array(z.number()), z.number(), usd.nullable(), z.boolean(), z.boolean(),
]);
const arbMarketTypeSchema = z.enum(["game", "player"]);
const arbRowSchema = z.object({
  id: arbRowIdSchema, event: eventIdSchema, kind: arbKindSchema, market: z.string(), market_key: z.string().min(1), market_type: arbMarketTypeSchema,
  entity: arbEntitySchema, team: z.string(),
  legs: z.array(arbLegSchema).min(2), picks: z.array(arbPickSchema),
}).refine((row) => row.picks.every(([legs, stakes]) => legs.length >= 2 && new Set(legs).size === legs.length && legs.length === stakes.length && legs.every((index) => index < row.legs.length) && stakes.every((share) => share > 0) && Math.abs(stakes.reduce((sum, share) => sum + share, 0) - 1) < 1e-3));
const lowHoldRowSchema = z.object({
  id: arbRowIdSchema, event: eventIdSchema, kind: arbKindSchema, market: z.string(), market_key: z.string().min(1), market_type: arbMarketTypeSchema,
  entity: arbEntitySchema, team: z.string(),
  hold: z.number(), books: count,
  legs: z.array(arbLegSchema).min(2),
});
const looseRowsSnapSchema = z.object({ events: z.array(eventSchema), rows: z.array(z.unknown()) });
const looseRowsDeltaSchema = z.object({
  events: z.array(eventSchema).default([]), rows: z.array(z.unknown()).default([]),
  drop_rows: z.array(z.string().min(1)).default([]), drop_events: z.array(eventIdSchema).default([]),
});
const looseRowIdSchema = z.object({ id: arbRowIdSchema });
const cellKeySchema = z.string().regex(CELL_KEY);
const whalesSideSchema = z.tuple([z.string().min(1), oddsSchema.nullable(), usd, count]);
const whalesEntrySchema = z.tuple([cellKeySchema, whalesSideSchema, whalesSideSchema]);
const whalesFairSchema = z.number().gt(0).lt(1);
const whalesFairEntrySchema = z.tuple([cellKeySchema, whalesFairSchema, whalesFairSchema]);
const whalesFairWithoutSchema = z.union([z.tuple([cellKeySchema, slugSchema, whalesFairSchema, whalesFairSchema]), z.tuple([cellKeySchema, slugSchema])]);
const whalesWithoutSchema = z.tuple([cellKeySchema, slugSchema, whalesSideSchema, whalesSideSchema]);
const whalesRowsSnapSchema = looseRowsSnapSchema.extend({ whales: z.array(whalesEntrySchema).default([]), whales_fair: z.array(whalesFairEntrySchema).default([]), whales_without: z.array(whalesWithoutSchema).default([]), whales_fair_without: z.array(whalesFairWithoutSchema).default([]) });
const whalesRowsDeltaSchema = looseRowsDeltaSchema.extend({ whales: z.array(whalesEntrySchema).default([]), whales_fair: z.array(whalesFairEntrySchema).default([]), whales_without: z.array(whalesWithoutSchema).default([]), whales_fair_without: z.array(whalesFairWithoutSchema).default([]), drop_whales: z.array(cellKeySchema).default([]) });
const whalesLadderSchema = z.object({ sides: z.array(z.tuple([z.string().min(1), z.array(z.tuple([slugSchema, oddsSchema, usd])), usd])) });

const toWhalesSide = ([name, odds, money, count]: z.infer<typeof whalesSideSchema>, fair: number | null): WhalesSide => ({ name, odds, money, count, fair });

type WhalesFairs = readonly [number, number] | null;
type WhalesSides = readonly [z.infer<typeof whalesSideSchema>, z.infer<typeof whalesSideSchema>];

function whalesSummaryOf([a, b]: WhalesSides, fair: WhalesFairs): WhalesSummary {
  return [toWhalesSide(a, fair?.[0] ?? null), toWhalesSide(b, fair?.[1] ?? null)];
}

function whalesEntriesOf(message: { readonly whales: readonly z.infer<typeof whalesEntrySchema>[]; readonly whales_fair: readonly z.infer<typeof whalesFairEntrySchema>[]; readonly whales_without: readonly z.infer<typeof whalesWithoutSchema>[]; readonly whales_fair_without: readonly z.infer<typeof whalesFairWithoutSchema>[] }, topic: Topic): (readonly [CellKey, WhalesCell])[] {
  const keys = new Set(message.whales.map(([key]) => key));
  const fairs = new Map<CellKey, WhalesFairs>();
  for (const [key, a, b] of message.whales_fair) {
    if (fairs.has(key) || !keys.has(key)) throw new ProtocolError(`Invalid ${topic.kind} whales_fair data`, topic);
    fairs.set(key, [a, b]);
  }
  const without = new Map<CellKey, Map<BookId, WhalesFairs>>();
  for (const [key, book, a, b] of message.whales_fair_without) {
    const books = without.get(key) ?? new Map<BookId, WhalesFairs>();
    if (!keys.has(key) || books.has(book)) throw new ProtocolError(`Invalid ${topic.kind} whales_fair_without data`, topic);
    books.set(book, a === undefined || b === undefined ? null : [a, b]);
    without.set(key, books);
  }
  const sidesWithout = new Map<CellKey, Map<BookId, WhalesSides>>();
  for (const [key, book, a, b] of message.whales_without) {
    const books = sidesWithout.get(key) ?? new Map<BookId, WhalesSides>();
    if (!keys.has(key) || books.has(book)) throw new ProtocolError(`Invalid ${topic.kind} whales_without data`, topic);
    books.set(book, [a, b]);
    sidesWithout.set(key, books);
  }
  return message.whales.map(([key, a, b]) => {
    const fair = fairs.get(key) ?? null;
    const fairWithout = without.get(key);
    const sides = sidesWithout.get(key);
    const books = new Map<BookId, WhalesSummary>();
    for (const book of new Set([...(sides?.keys() ?? []), ...(fairWithout?.keys() ?? [])])) {
      books.set(book, whalesSummaryOf(sides?.get(book) ?? [a, b], fairWithout?.has(book) ? fairWithout.get(book) ?? null : fair));
    }
    return [key, { summary: whalesSummaryOf([a, b], fair), without: books }];
  });
}

export const noRowBook = (): null => null;
export const sniperRowBook = (row: SniperRow): BookId => row.book;

export function joinWhales<R extends PropsRow>(rows: ReadonlyMap<RowId, R>, cells: ReadonlyMap<CellKey, WhalesCell>, keys: ReadonlySet<CellKey> | null, bookOf: (row: R) => BookId | null): ReadonlyMap<RowId, R> {
  let joined: Map<RowId, R> | null = null;
  for (const [id, row] of rows) {
    if (row.cell === null || (keys !== null && !keys.has(row.cell))) continue;
    const cell = cells.get(row.cell);
    const book = bookOf(row);
    const whales = cell === undefined ? null : (book === null ? undefined : cell.without.get(book)) ?? cell.summary;
    if (row.whales !== whales) (joined ??= new Map(rows)).set(id, { ...row, whales });
  }
  return joined ?? rows;
}
const toWhalesLadder = (view: z.infer<typeof whalesLadderSchema>): WhalesLadder => ({
  sides: view.sides.map(([name, orders, total]) => ({ name, orders: orders.map(([book, odds, stake]) => ({ book, odds, stake })), total })),
});

const ladderOutcomeSchema = z.object({
  odds: oddsSchema, stake: usd.optional(), depth: z.array(z.tuple([oddsSchema, usd])).optional(),
  link: z.string().optional(), app: z.string().optional(), sel: z.string().optional(), side: z.string().optional(),
});
const slotSchema = z.number().int().nonnegative();
const ladderLineSchema = z.object({
  slot: slotSchema, line: lineSchema,
  books: z.array(z.object({ book: slugSchema, outcomes: z.array(ladderOutcomeSchema.nullable()).min(1).max(3) })),
});
const ladderSnapSchema = z.object({ mains: z.array(lineSchema.nullable()), lines: z.array(ladderLineSchema) });
const ladderDeltaSchema = z.object({
  mains: z.array(z.tuple([slotSchema, lineSchema.nullable()])).default([]),
  lines: z.array(ladderLineSchema).default([]),
  drop_lines: z.array(z.tuple([slotSchema, lineSchema])).default([]),
});

const whaleStateSchema = z.enum(["fresh", "stale", "unavailable"]);
const whaleSideSchema = z.enum(["OVER", "UNDER", "HOME", "AWAY", "DRAW", "YES", "NO"]);
const americanOdds = z.number().int();
const whaleSideFields = { side: whaleSideSchema, line: lineSchema, demand: usd, money_at_risk: usd.optional() };
const servedEdge = { fair_odds: americanOdds.optional(), edge_percent: z.number().nullish() };
function edgeIf<T extends { edge_percent?: number | null }>({ edge_percent, ...rest }: T, graded: boolean): Omit<T, "edge_percent"> & { edge_percent?: number } {
  return edge_percent === null || edge_percent === undefined || !graded ? rest : { ...rest, edge_percent };
}
function hasFair(row: { fair_odds?: number; edge_percent?: number | null }): boolean {
  return row.fair_odds !== 0 && row.edge_percent !== null && row.edge_percent !== undefined;
}
function edgeIfFair<T extends { fair_odds?: number; edge_percent?: number | null }>(row: T): Omit<T, "edge_percent"> & { edge_percent?: number } {
  return edgeIf(row, hasFair(row));
}
function pricesIfFair<T extends { fair_odds?: number; edge_percent?: number | null; recommended_side: { prices: readonly { edge_percent?: number }[] } }>(row: T) {
  const graded = hasFair(row);
  return { ...edgeIf(row, graded), recommended_side: { ...row.recommended_side, prices: row.recommended_side.prices.map((price) => edgeIf(price, graded)) } };
}
const whaleRowSchema = z.object({
  market_data: z.object({
    id: whaleRowIdSchema, event_id: eventIdSchema, event: z.string(), event_start: z.string(),
    home_team: z.string(), away_team: z.string(), league: z.string(), sport: z.string(),
    entity_type: z.string(), entity_name: z.string(), market: z.string(), market_type: z.string(),
    line: lineSchema, market_arity: count.optional(), updated_at: z.string(),
    image_url: z.string().optional(), home_logo_url: z.string().optional(), away_logo_url: z.string().optional(),
  }),
  best_book: slugSchema, best_odds: americanOdds, fair_outcome_share: probability.optional(),
  ...servedEdge, edge_supporting_liquidity: usd, same_side_conviction_liquidity: usd,
  recommended_side: z.object({
    ...whaleSideFields,
    prices: z.array(z.object({
      book: slugSchema, odds_american: americanOdds, size: usd.nullish().transform((size) => size ?? null), bet_link: z.string(), bet_link_mobile: z.string().optional(),
      edge_percent: z.number().nullish(), edge_supporting_liquidity: usd, fee_adjusted: z.boolean().optional(),
    }).transform((price) => edgeIf(price, true))),
  }),
  opposite_side: z.object({ ...whaleSideFields, peer_books: z.array(slugSchema) }),
}).transform(pricesIfFair);
const leaguesSchema = z.array(z.string());
const whaleSnapSchema = z.object({ state: whaleStateSchema, missing: leaguesSchema.default([]), cut_lowest_edge: count, rows: z.array(whaleRowSchema) });
const whaleDeltaSchema = z.object({
  rows: z.array(whaleRowSchema).default([]), drop_rows: z.array(whaleRowIdSchema).default([]),
  state: whaleStateSchema.optional(), missing: leaguesSchema.optional(), cut_lowest_edge: count.optional(),
});

const orderLevelSchema = z.object({
  odds: z.number(), liquidity: z.number(), actual_money_at_risk: z.number().optional(),
  maker_odds: z.number().optional(), maker_stake: z.number().optional(),
});
const bookDataSchema = z.object({
  selection_id: z.string().optional(), odds_decimal: z.number(), odds_american: americanOdds,
  liquidity: z.number().default(0), actual_money_at_risk: z.number(), stake_required: z.number().optional(),
  order_book: z.array(orderLevelSchema).optional(), total_liquidity: z.number().optional(), bet_link: z.string().optional(), bet_link_mobile: z.string().optional(),
  fee_adjusted: z.boolean().optional(),
});
const peerSchema = z.object({
  book: z.string(), selection_id: z.string().optional(), odds_american: americanOdds, odds_decimal: z.number().gt(1),
  limit: z.number().optional(), opp_odds_american: americanOdds.optional(), opp_odds_decimal: z.number().optional(),
  bet_link: z.string().optional(), bet_link_mobile: z.string().optional(), fee_adjusted: z.boolean().optional(),
});
const sideDataSchema = z.object({
  side: whaleSideSchema, line: lineSchema,
  books: z.record(z.string(), bookDataSchema).nullable().transform((books) => books ?? {}),
  peers: z.array(peerSchema).optional(),
});
const opportunitySchema = z.object({
  market_data: z.object({
    id: whaleRowIdSchema, event_id: eventIdSchema, event: z.string(), event_start: z.string(),
    home_team: z.string(), away_team: z.string(), league: z.string(), sport: z.string(),
    entity_type: z.string(), entity_name: z.string(), market: z.string(), market_type: z.string(),
    line: lineSchema, market_arity: count.optional(), total_liquidity: z.number(),
    sides: z.array(sideDataSchema).optional(), side_a: sideDataSchema.nullable().default(null), side_b: sideDataSchema.nullable().default(null),
    updated_at: z.string(), image_url: z.string().optional(), home_logo_url: z.string().optional(), away_logo_url: z.string().optional(),
  }),
  recommended_side: sideDataSchema, opposite_side: sideDataSchema,
  best_book: z.string(), best_odds: americanOdds, ...servedEdge, fair_odds: americanOdds,
  edge_supporting_liquidity: z.number(), same_side_conviction_liquidity: z.number(),
  whale_volume: z.number(), whale_volume_mode: z.string(), fair_outcome_share: z.number().optional(),
}).transform(edgeIfFair);
const whaleRowSnapSchema = z.object({ state: whaleStateSchema, row: opportunitySchema });
const whaleRowDeltaSchema = z.object({ row: opportunitySchema.optional(), state: whaleStateSchema.optional() });

type WireOpportunity = z.output<typeof opportunitySchema>;
type WireSide = z.output<typeof sideDataSchema>;
type WireBook = WireSide["books"][string];

function withMakerLevels(row: WireOpportunity): { readonly row: APIOpportunity; readonly rejected: RejectedRow[] } {
  const reasons = new Set<string>();
  const book = (side: WireSide["side"], name: string, { order_book, ...data }: WireBook): BookData => {
    if (EXCHANGE_BOOKS.has(name.toLowerCase()) && data.liquidity > 0 && !order_book?.length) reasons.add(`${side}.${name}: liquidity without order-book levels`);
    if (order_book === undefined) return data;
    return {
      ...data,
      order_book: order_book.flatMap(({ maker_odds, maker_stake, ...level }) => {
        if (maker_odds === undefined || maker_odds <= 1 || (maker_stake !== undefined && maker_stake <= 0)) {
          reasons.add(`${side}.${name}: an order-book level without a valid maker price`);
          return [];
        }
        return maker_stake === undefined ? [] : [{ ...level, maker_odds, maker_stake }];
      }),
    };
  };
  const side = (wire: WireSide): SideData => ({
    ...wire,
    books: Object.fromEntries(Object.entries(wire.books).map(([name, data]) => [name, book(wire.side, name, data)])),
  });
  const market = row.market_data;
  return {
    row: {
      ...row,
      market_data: { ...market, sides: market.sides?.map(side), side_a: market.side_a && side(market.side_a), side_b: market.side_b && side(market.side_b) },
      recommended_side: side(row.recommended_side),
      opposite_side: side(row.opposite_side),
    },
    rejected: [...reasons].map((reason) => ({ id: market.id, reason })),
  };
}

const inplayIdentityFields = { id: eventIdSchema, name: z.string().optional(), home: z.string().optional(), away: z.string().optional(), home_short: z.string().optional(), away_short: z.string().optional(), home_logo_url: z.string().optional(), away_logo_url: z.string().optional(), game_families: z.array(slugSchema).optional(), start: epochMs, families: z.array(slugSchema) };
const inplayIdentitySchema = z.object(inplayIdentityFields);
const liveClockSchema = z.object({ seconds: count, running: z.boolean().optional(), at_ms: epochMs })
  .transform((c): LiveClock => ({ seconds: c.seconds, running: c.running ?? false, atMs: c.at_ms }))
  .nullable().catch(null);
const liveSideSchema = z.enum(["home", "away"]);
const liveSetsSchema = z.object({
  games: z.array(z.object({ home: count, away: count })).min(1).max(5),
  home_points: z.string().optional(), away_points: z.string().optional(), serving: liveSideSchema.optional(),
})
  .refine((s) => (s.home_points === undefined) === (s.away_points === undefined))
  .transform((s): LiveSets => ({ games: s.games, homePoints: s.home_points ?? "", awayPoints: s.away_points ?? "", serving: s.serving ?? null }))
  .nullable().catch(null);
const livePlaySchema = z.object({ text: z.string().min(1).refine((text) => !/\p{Cc}/u.test(text) && utf8.encode(text).length <= 160), at_ms: epochMs })
  .transform((p): LivePlay => ({ text: p.text, atMs: p.at_ms }));
const livePauseSchema = z.object({ kind: z.string().regex(/^[a-z_]{1,32}$/) })
  .transform((p): LivePause => ({ kind: LIVE_PAUSE_KINDS.find((kind) => kind === p.kind) ?? "other" }))
  .nullable().catch(null);
const periodScore = z.number().int().min(0).max(999);
const livePeriodsSchema = z.array(z.object({ home: periodScore, away: periodScore })).min(1).max(20).nullable();
const liveStatusSchema = z.object({
  period: livePeriodSchema.optional(), clock: liveClockSchema.optional(), home_score: z.string().optional(), away_score: z.string().optional(), sets: liveSetsSchema.optional(),
  periods: livePeriodsSchema.optional(), pause: livePauseSchema.optional(), play: livePlaySchema.optional().catch(undefined), paused: z.boolean().optional(),
}).transform((s): InplayStatus => ({
  period: s.period ?? null, clock: s.clock ?? null, score: s.home_score && s.away_score ? { home: s.home_score, away: s.away_score } : null,
  sets: s.sets ?? null, periods: s.periods ?? null, pause: s.pause ?? null, plays: s.play === undefined ? [] : [s.play], paused: s.paused ?? false,
})).nullable();
const inplayListSnapSchema = z.object({ events: z.array(z.object({ ...inplayIdentityFields, status: liveStatusSchema.optional() })) });
const inplayListDeltaSchema = z.object({
  events: z.array(inplayIdentitySchema).default([]), status: z.array(z.tuple([eventIdSchema, liveStatusSchema])).default([]), drop_events: z.array(eventIdSchema).default([]),
});
const decimalMicros = z.number().int().nonnegative();
const inplayCellSchema = z.object({
  cell: z.string().min(1), book: slugSchema, side: z.string(), market: z.string(), entity: z.string().optional(), entity_key: entityKeySchema.min(1).optional(),
  line: lineSchema.optional(), alt: z.boolean().optional(), price_decimal_micros: decimalMicros, suspended: z.boolean(), stale: z.boolean(),
  link: z.string().optional(), app: z.string().optional(),
}).transform(({ entity: name, entity_key: key, ...cell }, ctx) => {
  if (key === undefined) return { ...cell, entity: null };
  if (name === undefined) {
    ctx.addIssue({ code: "custom", message: "a keyed cell must name its entity" });
    return z.NEVER;
  }
  return { ...cell, entity: { key, name } };
});
const inplayEventSnapSchema = z.object({ cells: z.array(inplayCellSchema) });
const inplayEventDeltaSchema = z.object({
  cells: z.array(z.tuple([z.string().min(1), z.unknown(), decimalMicros, z.boolean(), z.boolean()])).default([]),
  add: z.array(inplayCellSchema).default([]), drop: z.array(z.string().min(1)).default([]),
});

const envelopeFields = { topic: wireTopicSchema, seq: count, src: epochMs, ts: epochMs, data: z.unknown() };
const serverMessageSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("auth"), exp: epochMs, now: epochMs }),
  z.object({ type: z.literal("snap"), ...envelopeFields }),
  z.object({ type: z.literal("delta"), ...envelopeFields, from: count.optional() }),
  z.object({ type: z.literal("end"), topic: wireTopicSchema, reason: z.enum(END_REASONS) }),
  z.object({ type: z.literal("hb"), now: epochMs }),
  z.object({ type: z.literal("pong"), n: count, now: epochMs }),
  z.object({ type: z.literal("error"), code: z.string().min(1) }),
]);
const knownTypes = new Set(["auth", "snap", "delta", "end", "hb", "pong", "error"]);

const fatalCodes = [
  "auth_required", "ticket_invalid", "ticket_expired", "auth_mismatch", "ticket_replayed",
  "rate_limited", "subject_limit", "command_invalid", "server_shutdown",
] as const;
export type FatalCode = (typeof fatalCodes)[number];
const fatalCodeSet: ReadonlySet<string> = new Set(fatalCodes);

function isFatalCode(code: string): code is FatalCode {
  return fatalCodeSet.has(code);
}

export type BoardDelta = {
  readonly events: readonly EventHeader[];
  readonly rows: readonly BoardRow[];
  readonly dropRows: readonly RowId[];
  readonly dropEvents: readonly EventId[];
};
export type LadderDelta = {
  readonly mains: readonly (readonly [number, number | null])[];
  readonly lines: readonly LadderLine[];
  readonly dropLines: readonly (readonly [number, number])[];
};
export type CatalogDelta = { readonly leagues: readonly LeagueSummary[] };
export type DfsDelta = { readonly leagues: readonly DfsLeagueEntry[] };
export type PropsDelta = {
  readonly events: readonly EventHeader[];
  readonly rows: readonly PropsRow[];
  readonly dropRows: readonly RowId[];
  readonly dropEvents: readonly EventId[];
  readonly whales: readonly (readonly [CellKey, WhalesCell])[];
  readonly dropWhales: readonly CellKey[];
};

export type SniperDelta = {
  readonly events: readonly EventHeader[];
  readonly rows: readonly SniperRow[];
  readonly dropRows: readonly RowId[];
  readonly dropEvents: readonly EventId[];
  readonly whales: readonly (readonly [CellKey, WhalesCell])[];
  readonly dropWhales: readonly CellKey[];
};
export type DiscrepanciesDelta = {
  readonly events: readonly EventHeader[];
  readonly rows: readonly DiscrepancyRow[];
  readonly dropRows: readonly RowId[];
  readonly dropEvents: readonly EventId[];
};

export type ArbitrageDelta = {
  readonly events: readonly EventHeader[];
  readonly rows: readonly ArbRow[];
  readonly dropRows: readonly RowId[];
  readonly dropEvents: readonly EventId[];
};

export type LowHoldDelta = {
  readonly events: readonly EventHeader[];
  readonly rows: readonly LowHoldRow[];
  readonly dropRows: readonly RowId[];
  readonly dropEvents: readonly EventId[];
};

export type WhaleDelta = {
  readonly rows: readonly WhaleRow[];
  readonly dropRows: readonly RowId[];
  readonly state: WhaleState | null;
  readonly missing: readonly string[] | null;
  readonly cutLowestEdge: number | null;
};
export type WhaleRowDelta = { readonly row: WhaleDetail["row"] | null; readonly state: WhaleState | null };
export type InplayListDelta = {
  readonly games: readonly Omit<InplayGame, "status">[];
  readonly statuses: readonly (readonly [EventId, InplayStatus | null])[];
  readonly dropGames: readonly EventId[];
};
export type InplayPrice = Pick<InplayCell, "id" | "odds" | "suspended" | "stale">;
export type InplayEventDelta = { readonly prices: readonly InplayPrice[]; readonly add: readonly InplayCell[]; readonly drop: readonly string[] };

export type RejectedRow = { readonly id: RowId | null; readonly reason: string };

export type TopicPayload =
  | { readonly kind: "catalog"; readonly snap: Catalog }
  | { readonly kind: "dfs"; readonly snap: Dfs }
  | { readonly kind: "board"; readonly snap: Board }
  | { readonly kind: "ladder"; readonly snap: Ladder }
  | { readonly kind: "props"; readonly snap: Props }
  | { readonly kind: "whale"; readonly snap: WhaleList }
  | { readonly kind: "whale_row"; readonly snap: WhaleDetail; readonly rejected: readonly RejectedRow[] }
  | { readonly kind: "discrepancies"; readonly snap: Discrepancies }
  | { readonly kind: "arbitrage"; readonly snap: Arbitrage; readonly rejected: readonly RejectedRow[] }
  | { readonly kind: "lowhold"; readonly snap: LowHold; readonly rejected: readonly RejectedRow[] }
  | { readonly kind: "sniper"; readonly snap: Sniper }
  | { readonly kind: "race"; readonly snap: RaceList }
  | { readonly kind: "live"; readonly snap: InplayList }
  | { readonly kind: "live_event"; readonly snap: InplayEvent }
  | { readonly kind: "whales"; readonly snap: WhalesLadder };
export type TopicDeltaPayload =
  | { readonly kind: "catalog"; readonly delta: CatalogDelta }
  | { readonly kind: "dfs"; readonly delta: DfsDelta }
  | { readonly kind: "board"; readonly delta: BoardDelta }
  | { readonly kind: "ladder"; readonly delta: LadderDelta }
  | { readonly kind: "props"; readonly delta: PropsDelta }
  | { readonly kind: "whale"; readonly delta: WhaleDelta }
  | { readonly kind: "whale_row"; readonly delta: WhaleRowDelta; readonly rejected: readonly RejectedRow[] }
  | { readonly kind: "discrepancies"; readonly delta: DiscrepanciesDelta }
  | { readonly kind: "arbitrage"; readonly delta: ArbitrageDelta; readonly rejected: readonly RejectedRow[] }
  | { readonly kind: "lowhold"; readonly delta: LowHoldDelta; readonly rejected: readonly RejectedRow[] }
  | { readonly kind: "sniper"; readonly delta: SniperDelta }
  | { readonly kind: "race"; readonly delta: RaceDelta }
  | { readonly kind: "live"; readonly delta: InplayListDelta }
  | { readonly kind: "live_event"; readonly delta: InplayEventDelta }
  | { readonly kind: "whales"; readonly delta: WhalesLadder };

export type ServerMessage =
  | { readonly type: "auth"; readonly expMs: number; readonly serverNowMs: number }
  | { readonly type: "snap"; readonly topic: Topic; readonly seq: number; readonly srcMs: number; readonly tsMs: number; readonly payload: TopicPayload }
  | { readonly type: "delta"; readonly topic: Topic; readonly seq: number; readonly from: number; readonly srcMs: number; readonly tsMs: number; readonly payload: TopicDeltaPayload }
  | { readonly type: "end"; readonly topic: Topic; readonly reason: EndReason }
  | { readonly type: "hb"; readonly serverNowMs: number }
  | { readonly type: "pong"; readonly n: number; readonly serverNowMs: number }
  | { readonly type: "error"; readonly code: FatalCode | "unknown" };

export class ProtocolError extends Error {
  readonly topic: Topic | null;

  constructor(message: string, topic: Topic | null = null, cause?: unknown) {
    super(message, { cause });
    this.name = "ProtocolError";
    this.topic = topic;
  }
}

function parseData<S extends z.ZodType>(schema: S, data: unknown, topic: Topic, type: string): z.output<S> {
  const parsed = schema.safeParse(data);
  if (!parsed.success) throw new ProtocolError(`Invalid ${topic.kind} ${type} data`, topic);
  return parsed.data;
}

function toLeague(league: z.infer<typeof leagueSchema>): LeagueSummary {
  return {
    id: league.id, status: league.status, staleBooks: new Set(league.stale_books),
    events: league.events, families: league.families.families, skippedFamilies: league.families.skipped,
  };
}

function toEvent(event: z.infer<typeof eventSchema>): EventHeader {
  return {
    id: event.id, name: event.name, startMs: event.start, home: event.home, away: event.away,
    homeShort: event.home_short || null, awayShort: event.away_short || null, homeLogoUrl: event.home_logo_url || null, awayLogoUrl: event.away_logo_url || null,
    ...(event.home_rank === undefined ? {} : { homeRank: event.home_rank }),
    ...(event.away_rank === undefined ? {} : { awayRank: event.away_rank }),
    ...(event.home_conf === undefined ? {} : { homeConf: event.home_conf }),
    ...(event.away_conf === undefined ? {} : { awayConf: event.away_conf }),
    props: event.props,
  };
}

function toTopicEvent(event: z.infer<typeof eventSchema>, topic: SniperRowsTopic): EventHeader {
  if (topic.kind !== "sniper_live") return toEvent(event);
  const period = event.period ?? null;
  return { ...toEvent(event), live: { ...IN_PLAY, period, pause: period?.break ? PERIOD_BREAK : null } };
}

function toCell([book, odds, off, stakes]: z.infer<typeof bookCellSchema>): BookCell {
  return off === null || off === undefined
    ? { at: "main", book, odds, stakes: stakes ?? null }
    : { at: "off", book, line: off, odds, stakes: stakes ?? null };
}

function toRow(row: z.infer<typeof rowSchema>): BoardRow {
  return {
    id: row.id, eventId: row.event,
    entity: row.entity === null ? null : { key: row.entity.key, name: row.entity.name, ...(row.entity.order === undefined ? {} : { order: row.entity.order }) },
    player: row.entity === null ? null : playerOf(row.entity),
    markets: row.markets.map((market) => market && {
      main: market.main ?? null, lines: market.lines, books: market.books.map(toCell),
      ...(market.consensus === undefined ? {} : { consensus: market.consensus }),
    }),
  };
}

function decodeRowMap<T extends { readonly id: RowId }>(rows: readonly unknown[], decode: (row: unknown) => T | typeof UNKNOWN_KIND | null, topic: Topic): Map<RowId, T> {
  const decoded = new Map<RowId, T>();
  for (const row of rows) {
    const value = decode(row);
    if (value === UNKNOWN_KIND) continue;
    if (value === null) throw new ProtocolError(`Invalid ${topic.kind} snap data`, topic);
    decoded.set(value.id, value);
  }
  return decoded;
}

function decodeRows<T extends object>(rows: readonly unknown[], decode: (row: unknown) => T | typeof UNKNOWN_KIND | null, topic: Topic): T[] {
  const decoded: T[] = [];
  for (const row of rows) {
    const value = decode(row);
    if (value === UNKNOWN_KIND) continue;
    if (value === null) throw new ProtocolError(`Invalid ${topic.kind} delta data`, topic);
    decoded.push(value);
  }
  return decoded;
}

function toDiscrepancyBook([book, line, overOdds, underOdds, multiplier, overSelectionId, underSelectionId, overLinks, underLinks, overFair, underFair]: z.infer<typeof discrepancyBookSchema>): DiscrepancyBook {
  return { book, line, overOdds, underOdds, multiplier, overSelectionId, underSelectionId, overLinks: expandLinks(overLinks), underLinks: expandLinks(underLinks), overFair, underFair };
}

function toDiscrepancyRow(row: z.infer<typeof discrepancyRowSchema>, league: DiscrepancyRow["league"]): DiscrepancyRow {
  return {
    id: row.id, eventId: row.event, league, marketKey: row.market_key, market: row.market,
    entity: { key: row.entity.key, name: row.entity.name }, player: playerOf(row.entity), team: row.team,
    modal: row.modal, min: row.min, max: row.max, spread: row.spread,
    books: row.books.map(toDiscrepancyBook),
    best: { book: row.best.book, line: row.best.line, side: row.best.side, fair: row.best.fair ?? null },
    fairModal: row.fair_modal ?? null, peers: row.peers,
  };
}

function toArbLeg([book, side, market, line, odds, limit, selection, links, fair, buyNo, feeIn]: z.infer<typeof arbLegSchema>): ArbLeg {
  return { book, side, market, line, odds, limit, selection, links: expandLinks(links), fair, buyNo, feeIn };
}

function toArbPick([legs, stakes, roi, maxStake, middle, check]: z.infer<typeof arbPickSchema>): ArbPick {
  return { legs, stakes, roi, maxStake, middle, check };
}

function toArbRow(row: z.infer<typeof arbRowSchema>, league: ArbRow["league"]): ArbRow {
  return {
    id: row.id, eventId: row.event, league, kind: row.kind, marketKey: row.market_key, marketType: row.market_type, market: row.market,
    entity: { key: row.entity.key, name: row.entity.name }, player: playerOf(row.entity), team: row.team,
    legs: row.legs.map(toArbLeg), picks: row.picks.map(toArbPick),
  };
}

function toLowHoldRow(row: z.infer<typeof lowHoldRowSchema>, league: LowHoldRow["league"]): LowHoldRow {
  return {
    id: row.id, eventId: row.event, league, kind: row.kind, marketKey: row.market_key, marketType: row.market_type, market: row.market,
    entity: { key: row.entity.key, name: row.entity.name }, player: playerOf(row.entity), team: row.team,
    hold: row.hold, books: row.books, legs: row.legs.map(toArbLeg),
  };
}

function validRows<S extends z.ZodType>(schema: S, rows: readonly unknown[]): { readonly valid: z.output<S>[]; readonly rejected: RejectedRow[] } {
  const valid: z.output<S>[] = [];
  const rejected: RejectedRow[] = [];
  for (const row of rows) {
    const parsed = schema.safeParse(row);
    if (parsed.success) {
      valid.push(parsed.data);
      continue;
    }
    const id = looseRowIdSchema.safeParse(row);
    const [issue] = parsed.error.issues;
    rejected.push({ id: id.success ? id.data.id : null, reason: `${issue.path.join(".")}: ${issue.message}` });
  }
  return { valid, rejected };
}

function rejectedIds(rejected: readonly RejectedRow[]): RowId[] {
  return rejected.flatMap(({ id }) => (id === null ? [] : [id]));
}

const noDepth: readonly (readonly [number, number])[] = [];

function toLadderLine(line: z.infer<typeof ladderLineSchema>): LadderLine {
  return {
    slot: line.slot, line: line.line,
    books: line.books.map((book) => ({
      book: book.book,
      outcomes: book.outcomes.map((outcome) => outcome && {
        odds: outcome.odds, stake: outcome.stake ?? null, depth: outcome.depth ?? noDepth,
        link: outcome.link || null, appLink: outcome.app || null,
        selectionId: outcome.sel || null, side: outcome.side || null,
      }),
    })),
  };
}

const MICROS = 1_000_000;

function toInplayIdentity(game: z.infer<typeof inplayIdentitySchema>): Omit<InplayGame, "status"> {
  return { id: game.id, name: game.name ?? "", home: game.home ?? "", away: game.away ?? "", homeShort: game.home_short ?? null, awayShort: game.away_short ?? null, homeLogoUrl: game.home_logo_url ?? null, awayLogoUrl: game.away_logo_url ?? null, gameFamilies: game.game_families ?? [], startMs: game.start, families: game.families };
}

function toInplayCell(cell: z.infer<typeof inplayCellSchema>): InplayCell {
  return {
    id: cell.cell, book: cell.book, side: cell.side, market: cell.market, entity: cell.entity, line: cell.line ?? null, alt: cell.alt ?? false,
    odds: cell.price_decimal_micros / MICROS, suspended: cell.suspended, stale: cell.stale,
    link: cell.link ?? null, appLink: cell.app ?? null,
  };
}

function snapPayload(topic: Topic, data: unknown): TopicPayload {
  switch (topic.kind) {
    case "catalog": {
      const catalog = parseData(catalogSnapSchema, data, topic, "snap");
      const leagues = catalog.leagues.map(toLeague);
      return {
        kind: "catalog",
        snap: {
          books: new Map(catalog.books.map((book) => [book.id, { stake: book.stake ?? null, standard: book.standard, resells: book.resells ?? null }])),
          leagues: new Map(leagues.map((league) => [league.id, league])),
          leagueOrder: leagues.map((league) => league.id),
        },
      };
    }
    case "dfs": {
      const leagues = dfsLeagues(parseData(dfsSchema, data, topic, "snap"));
      return { kind: "dfs", snap: new Map(leagues.flatMap(([id, league]) => (league === null ? [] : [[id, league] as const]))) };
    }
    case "board": {
      const board = parseData(boardSnapSchema, data, topic, "snap");
      return {
        kind: "board",
        snap: {
          events: new Map(board.events.map((event) => [event.id, toEvent(event)])),
          rows: new Map(board.rows.map((row) => [row.id, toRow(row)])),
        },
      };
    }
    case "props":
    case "optimizer": {
      const props = parseData(whalesRowsSnapSchema, data, topic, "snap");
      const whales = new Map(whalesEntriesOf(props, topic));
      return {
        kind: "props",
        snap: {
          events: new Map(props.events.map((event) => [event.id, toEvent(event)])),
          rows: joinWhales(decodeRowMap(props.rows, (row) => propsRowOf(row, topic.league), topic), whales, null, noRowBook),
          whales,
        },
      };
    }
    case "ladder": {
      const ladder = parseData(ladderSnapSchema, data, topic, "snap");
      const lines = ladder.lines.map(toLadderLine);
      return {
        kind: "ladder",
        snap: { mains: ladder.mains, lines: new Map(lines.map((line) => [ladderLineId(line.slot, line.line), line])) },
      };
    }
    case "whale": {
      const whale = parseData(whaleSnapSchema, data, topic, "snap");
      const rows = new Map<RowId, WhaleRow>();
      for (const row of whale.rows) rows.set(row.market_data.id, row);
      return { kind: "whale", snap: { state: whale.state, missing: whale.missing, cutLowestEdge: whale.cut_lowest_edge, rows } };
    }
    case "whale_row": {
      const { state, row } = parseData(whaleRowSnapSchema, data, topic, "snap");
      const checked = withMakerLevels(row);
      return { kind: "whale_row", snap: { state, row: checked.row }, rejected: checked.rejected };
    }
    case "discrepancies": {
      const snap = parseData(discrepanciesSnapSchema, data, topic, "snap");
      return {
        kind: "discrepancies",
        snap: {
          events: new Map(snap.events.map((event) => [event.id, toEvent(event)])),
          rows: new Map(snap.rows.map((row) => [row.id, toDiscrepancyRow(row, topic.league)])),
        },
      };
    }
    case "arbitrage": {
      const snap = parseData(looseRowsSnapSchema, data, topic, "snap");
      const rows = validRows(arbRowSchema, snap.rows);
      return {
        kind: "arbitrage",
        snap: {
          events: new Map(snap.events.map((event) => [event.id, toEvent(event)])),
          rows: new Map(rows.valid.map((row) => [row.id, toArbRow(row, topic.league)])),
        },
        rejected: rows.rejected,
      };
    }
    case "lowhold": {
      const snap = parseData(looseRowsSnapSchema, data, topic, "snap");
      const rows = validRows(lowHoldRowSchema, snap.rows);
      return {
        kind: "lowhold",
        snap: {
          events: new Map(snap.events.map((event) => [event.id, toEvent(event)])),
          rows: new Map(rows.valid.map((row) => [row.id, toLowHoldRow(row, topic.league)])),
        },
        rejected: rows.rejected,
      };
    }
    case "sniper":
    case "sniper_live": {
      const snap = parseData(whalesRowsSnapSchema, data, topic, "snap");
      const whales = new Map(whalesEntriesOf(snap, topic));
      return {
        kind: "sniper",
        snap: {
          events: new Map(snap.events.map((event) => [event.id, toTopicEvent(event, topic)])),
          rows: joinWhales(decodeRowMap(snap.rows, (row) => sniperRowOf(row, topic.league), topic), whales, null, sniperRowBook),
          whales,
        },
      };
    }
    case "race": {
      const snap = parseData(raceSnapSchema, data, topic, "snap");
      return { kind: "race", snap: { races: new Map(snap.races.map((race) => [race.id, toRace(race)])) } };
    }
    case "live": {
      const snap = parseData(inplayListSnapSchema, data, topic, "snap");
      return { kind: "live", snap: { games: new Map(snap.events.map((game) => [game.id, { ...toInplayIdentity(game), status: game.status ?? null }])), ended: new Set() } };
    }
    case "live_event": {
      const snap = parseData(inplayEventSnapSchema, data, topic, "snap");
      return { kind: "live_event", snap: { cells: new Map(snap.cells.map((cell) => [cell.cell, toInplayCell(cell)])) } };
    }
    case "whales":
      return { kind: "whales", snap: toWhalesLadder(parseData(whalesLadderSchema, data, topic, "snap")) };
  }
}

function deltaPayload(topic: Topic, data: unknown): TopicDeltaPayload {
  switch (topic.kind) {
    case "catalog":
      return { kind: "catalog", delta: { leagues: parseData(catalogDeltaSchema, data, topic, "delta").leagues.map(toLeague) } };
    case "dfs":
      return { kind: "dfs", delta: { leagues: dfsLeagues(parseData(dfsSchema, data, topic, "delta")) } };
    case "board": {
      const delta = parseData(boardDeltaSchema, data, topic, "delta");
      return {
        kind: "board",
        delta: { events: delta.events.map(toEvent), rows: delta.rows.map(toRow), dropRows: delta.drop_rows, dropEvents: delta.drop_events },
      };
    }
    case "props":
    case "optimizer": {
      const delta = parseData(whalesRowsDeltaSchema, data, topic, "delta");
      const rows = decodeRows(delta.rows, (row) => propsRowOf(row, topic.league), topic);
      return {
        kind: "props",
        delta: { events: delta.events.map(toEvent), rows, dropRows: delta.drop_rows, dropEvents: delta.drop_events, whales: whalesEntriesOf(delta, topic), dropWhales: delta.drop_whales },
      };
    }
    case "ladder": {
      const delta = parseData(ladderDeltaSchema, data, topic, "delta");
      return { kind: "ladder", delta: { mains: delta.mains, lines: delta.lines.map(toLadderLine), dropLines: delta.drop_lines } };
    }
    case "whale": {
      const delta = parseData(whaleDeltaSchema, data, topic, "delta");
      return {
        kind: "whale",
        delta: { rows: delta.rows, dropRows: delta.drop_rows, state: delta.state ?? null, missing: delta.missing ?? null, cutLowestEdge: delta.cut_lowest_edge ?? null },
      };
    }
    case "whale_row": {
      const delta = parseData(whaleRowDeltaSchema, data, topic, "delta");
      const checked = delta.row === undefined ? null : withMakerLevels(delta.row);
      return { kind: "whale_row", delta: { row: checked?.row ?? null, state: delta.state ?? null }, rejected: checked?.rejected ?? [] };
    }
    case "discrepancies": {
      const delta = parseData(discrepanciesDeltaSchema, data, topic, "delta");
      return {
        kind: "discrepancies",
        delta: { events: delta.events.map(toEvent), rows: delta.rows.map((row) => toDiscrepancyRow(row, topic.league)), dropRows: delta.drop_rows, dropEvents: delta.drop_events },
      };
    }
    case "arbitrage": {
      const delta = parseData(looseRowsDeltaSchema, data, topic, "delta");
      const rows = validRows(arbRowSchema, delta.rows);
      return {
        kind: "arbitrage",
        delta: { events: delta.events.map(toEvent), rows: rows.valid.map((row) => toArbRow(row, topic.league)), dropRows: [...delta.drop_rows, ...rejectedIds(rows.rejected)], dropEvents: delta.drop_events },
        rejected: rows.rejected,
      };
    }
    case "lowhold": {
      const delta = parseData(looseRowsDeltaSchema, data, topic, "delta");
      const rows = validRows(lowHoldRowSchema, delta.rows);
      return {
        kind: "lowhold",
        delta: { events: delta.events.map(toEvent), rows: rows.valid.map((row) => toLowHoldRow(row, topic.league)), dropRows: [...delta.drop_rows, ...rejectedIds(rows.rejected)], dropEvents: delta.drop_events },
        rejected: rows.rejected,
      };
    }
    case "sniper":
    case "sniper_live": {
      const delta = parseData(whalesRowsDeltaSchema, data, topic, "delta");
      const rows = decodeRows(delta.rows, (row) => sniperRowOf(row, topic.league), topic);
      return {
        kind: "sniper",
        delta: { events: delta.events.map((event) => toTopicEvent(event, topic)), rows, dropRows: delta.drop_rows, dropEvents: delta.drop_events, whales: whalesEntriesOf(delta, topic), dropWhales: delta.drop_whales },
      };
    }
    case "race": {
      const delta = parseData(raceDeltaSchema, data, topic, "delta");
      return { kind: "race", delta: { races: delta.races.map(toRace), drop: delta.drop ?? [] } };
    }
    case "live": {
      const delta = parseData(inplayListDeltaSchema, data, topic, "delta");
      return {
        kind: "live",
        delta: {
          games: delta.events.map(toInplayIdentity), statuses: delta.status, dropGames: delta.drop_events,
        },
      };
    }
    case "live_event": {
      const delta = parseData(inplayEventDeltaSchema, data, topic, "delta");
      return {
        kind: "live_event",
        delta: {
          prices: delta.cells.map(([id, , micros, suspended, stale]) => ({ id, odds: micros / MICROS, suspended, stale })),
          add: delta.add.map(toInplayCell), drop: delta.drop,
        },
      };
    }
    case "whales":
      return { kind: "whales", delta: toWhalesLadder(parseData(whalesLadderSchema, data, topic, "delta")) };
  }
}

function topicOf(json: object): Topic | null {
  const wire = wireTopicSchema.safeParse("topic" in json ? json.topic : undefined);
  return wire.success ? topicFromWire(wire.data) : null;
}

export function parseServerMessage(raw: string): ServerMessage | null {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch (error) {
    throw new ProtocolError("Server message is not JSON", null, error);
  }
  if (typeof json !== "object" || json === null || !("type" in json) || typeof json.type !== "string") {
    throw new ProtocolError("Server message has no type");
  }
  if (!knownTypes.has(json.type)) return null;
  const parsed = serverMessageSchema.safeParse(json);
  if (!parsed.success) throw new ProtocolError(`Invalid ${json.type} message`, topicOf(json));
  const message = parsed.data;
  switch (message.type) {
    case "auth": return { type: "auth", expMs: message.exp, serverNowMs: message.now };
    case "snap": {
      const topic = topicFromWire(message.topic);
      return { type: "snap", topic, seq: message.seq, srcMs: message.src, tsMs: message.ts, payload: snapPayload(topic, message.data) };
    }
    case "delta": {
      const topic = topicFromWire(message.topic);
      return { type: "delta", topic, seq: message.seq, from: message.from ?? message.seq - 1, srcMs: message.src, tsMs: message.ts, payload: deltaPayload(topic, message.data) };
    }
    case "end": return { type: "end", topic: topicFromWire(message.topic), reason: message.reason };
    case "hb": return { type: "hb", serverNowMs: message.now };
    case "pong": return { type: "pong", n: message.n, serverNowMs: message.now };
    case "error": return { type: "error", code: isFatalCode(message.code) ? message.code : "unknown" };
  }
}

export function isValidTopic(topic: Topic): boolean {
  return wireTopicSchema.safeParse(topicKey(topic)).success;
}

export type ClientCommand =
  | { readonly type: "auth"; readonly ticket: string }
  | { readonly type: "sub"; readonly topic: Topic }
  | { readonly type: "unsub"; readonly topic: Topic }
  | { readonly type: "ping"; readonly n: number };

export function encodeCommand(command: ClientCommand): string {
  switch (command.type) {
    case "auth": return JSON.stringify({ type: "auth", ticket: command.ticket });
    case "sub":
    case "unsub": return JSON.stringify({ type: command.type, topic: topicKey(command.topic) });
    case "ping": return JSON.stringify({ type: "ping", n: command.n });
  }
}

export const oddsScreenTicketSchema = z.object({
  ticket: z.string().min(1).max(8192),
  expires_at: z.string().datetime({ offset: true }),
});
export const oddsScreenSessionSchema = oddsScreenTicketSchema.extend({
  stream_url: z.url().refine((value) => value.startsWith("wss://") || value.startsWith("ws://")),
});

const searchEventSchema = eventSchema.extend({ league: slugSchema });
export const oddsScreenSearchSchema = z.object({
  games: z.array(searchEventSchema),
  players: z.array(z.object({
    league: slugSchema, key: entityKeySchema.min(1), name: z.string(), families: z.array(slugSchema), event: searchEventSchema,
  })),
});
